<template>
  <AppDialog
    v-if="currentPlan !== cheapestPlanName"
    class="archive-offer-dialog"
    position="centered"
    :title="t.dialogTitle"
    show
    @close="closeDialog"
  >
    <div class="title">Want to try a more affordable plan?</div>
    <p>Instead of {{ t.dialogAction }}, you can try a different plan at a lower cost.</p>
    <div class="offer">
      <div class="title">Change plan</div>
      <a
        :href="changePlanLink"
        class="compare-plans"
        @click.prevent="trackAndNavigate('Archive offer: Compare all plans clicked')"
      >
        Compare all plans
      </a>
      <div class="plans">
        <div class="plan current-plan">
          <div class="plan-name">{{ currentPlanInfo?.tier_title }}</div>
          <div class="plan-price">${{ currentPlanInfo?.price }}</div>
          <div class="plan-price-period">/seat/mo</div>
        </div>
        <span class="arrow hi-thin-right-arrow"></span>
        <div class="plan previous-plan">
          <div class="plan-name">{{ previousPlanInfo?.tier_title }}</div>
          <div class="plan-price">${{ previousPlanInfo?.price }}</div>
          <div class="plan-price-period">/seat/mo</div>
        </div>
        <template v-if="downgradeSuggestionName">
          <span class="divider"></span>
          <div class="plan left-border extra-plan">
            <div class="plan-name">{{ cheapestPlanName }}</div>
            <div class="plan-price">${{ cheapestPlanPrice }}</div>
            <div class="plan-price-period">/seat/mo</div>
          </div>
        </template>
      </div>
    </div>
    <div class="modal-footer d-flex justify-content-end pt-30 flex-wrap p-0">
      <button
        class="btn btn-default btn-default-hover-grey"
        @click="next"
      >
        {{ t.dialogTitle }}
      </button>
      <a
        class="btn btn-primary"
        :href="changePlanLink"
        @click.prevent="trackAndNavigate('Archive offer: Change plan clicked')"
      >
        Change plan
      </a>
    </div>
  </AppDialog>
</template>

<script>
import { AnalyticsResource, OrganizationsResource } from '~/resources';

const TEXT = {
  archive: {
    dialogTitle: 'Archive organization',
    dialogAction: 'archiving and cancelling your subscription',
  },
  cancel: {
    dialogTitle: 'Cancel subscription',
    dialogAction: 'cancelling your subscription',
  },
};

export default {
  name: 'ArchiveOfferDialog',
  props: {
    action: {
      type: String,
      default: 'archive',
    },
    currentPlan: {
      type: String,
      required: true,
    },
    organizationId: {
      type: Number,
      required: true,
    },
    plans: {
      type: Array,
      required: true,
    },
    source: {
      type: String,
      default: null,
    },
    offerSgtEssentialsPlan: {
      type: Boolean,
      default: false,
    },
    essentialsPlanPrice: {
      type: [Number, null],
      default: null,
    },
  },
  computed: {
    changePlanLink() {
      return OrganizationsResource.plansLink({ id: this.organizationId });
    },
    cheapestPlanName() {
      return this.offerSgtEssentialsPlan ? 'Essentials' : 'Starter';
    },
    t() {
      return TEXT[this.action];
    },
    currentPlanIndex() {
      return this.plans.findIndex((plan) => plan?.tier_title === this.currentPlan);
    },
    currentPlanInfo() {
      return this.plans[this.currentPlanIndex];
    },
    previousPlanInfo() {
      const immediatelyBelow = this.currentPlanIndex > 0 ? this.plans[this.currentPlanIndex - 1] : null;

      return this.resolvePreviousPlan(immediatelyBelow);
    },
    cheapestPlanPrice() {
      if (this.offerSgtEssentialsPlan && this.essentialsPlanPrice) {
        return this.essentialsPlanPrice;
      }

      return this.plans.find((plan) => plan.tier_title === this.cheapestPlanName)?.price;
    },
    downgradeSuggestionName() {
      if (this.currentPlan !== 'Team') {return null;}

      return this.offerSgtEssentialsPlan ? 'Essentials' : 'Starter';
    },
    sharedAnalyticsProperties() {
      return {
        organization_id: this.organizationId,
        action: this.action,
        ...(this.source && { source: this.source }),
      };
    },
  },
  mounted() {
    AnalyticsResource.create(null, {
      event_name: 'Archive offer viewed',
      event_properties: this.sharedAnalyticsProperties,
    }).catch((error) => console.error('Failed to track Archive offer viewed:', error));
  },
  methods: {
    resolvePreviousPlan(immediatelyBelow) {
      if (!this.offerSgtEssentialsPlan) {return immediatelyBelow;}

      if (immediatelyBelow?.tier_title === 'Starter') {
        const essentialsPlan = this.plans.find((plan) => plan.tier_title === 'Essentials');

        return essentialsPlan
          ? { ...essentialsPlan, price: this.essentialsPlanPrice ?? essentialsPlan.price }
          : immediatelyBelow;
      }

      if (immediatelyBelow?.tier_title === 'Essentials') {
        return { ...immediatelyBelow, price: this.essentialsPlanPrice ?? immediatelyBelow.price };
      }

      return immediatelyBelow;
    },
    next(event) {
      this.$emit('next', event);
    },
    closeDialog(event) {
      this.$emit('closeDialog', event);
    },
    trackAndNavigate(eventName) {
      AnalyticsResource.create(null, {
        event_name: eventName,
        event_properties: this.sharedAnalyticsProperties,
      }).catch((error) => console.error(`Failed to track ${eventName}:`, error));
      window.location = this.changePlanLink;
    },
  },
};
</script>
